/*
 * newsbot - Webhook action component
 */

import { FilterSection, FilterLayout, FilterHalfRow } from "@/components/filters/_components";
import { TextField, NumberField, TextAreaAutoResize } from "@components/inputs";
import { Select } from "@/components/inputs/select";
import type { FilterAction } from "@/types";
import { PayloadPreview } from "./PayloadPreview";

const HttpMethodOptions = [
  { label: "GET", value: "GET" },
  { label: "POST", value: "POST" },
  { label: "PUT", value: "PUT" },
  { label: "PATCH", value: "PATCH" },
  { label: "DELETE", value: "DELETE" },
];

interface WebhookProps {
  idx: number;
  action: FilterAction;
}

export const Webhook = ({ idx, action }: WebhookProps) => (
  <>
    <FilterSection
      title="Request"
      subtitle="Specify your request destination endpoint, headers and expected return status"
    >
      <FilterLayout>
        <TextField
          name={`actions.${idx}.webhook_endpoint`}
          label="Endpoint"
          columns={6}
          // aislop-ignore-next-line ai-slop/hardcoded-url -- example placeholder shown to the user in an input field
          placeholder="https://n8n.example.com/webhook/story"
          tooltip={<p>URL to your webhook endpoint. Pass params and set API tokens etc.</p>}
        />
        <Select
          name={`actions.${idx}.webhook_method`}
          label="HTTP method"
          optionDefaultText="Select http method"
          options={HttpMethodOptions}
          tooltip={<div><p>Select the HTTP method for this webhook. Defaults to POST</p></div>}
          columns={6}
        />
        <TextField
          name={`actions.${idx}.webhook_headers`}
          label="HTTP Request Headers (JSON)"
          columns={6}
          placeholder='{"Authorization": "Bearer token", "Content-Type": "application/json"}'
          tooltip={<p>JSON object with header key-value pairs</p>}
        />
        <NumberField
          name={`actions.${idx}.webhook_expected_status`}
          label="Expected HTTP status code"
          placeholder="200"
          tooltip={<p>Expected status code for successful webhook execution</p>}
        />
      </FilterLayout>
    </FilterSection>
    <FilterSection
      title="Retry"
      subtitle="Retry behavior on request failure"
    >
      <FilterLayout>
        <FilterHalfRow>
          <NumberField
            name={`actions.${idx}.webhook_retry_attempts`}
            label="Maximum retry attempts"
            placeholder="3"
          />
        </FilterHalfRow>
        <FilterHalfRow>
          <NumberField
            name={`actions.${idx}.webhook_retry_delay_seconds`}
            label="Retry delay in seconds"
            placeholder="5"
          />
        </FilterHalfRow>
      </FilterLayout>
    </FilterSection>
    <FilterSection
      title="Payload"
      subtitle="Specify your JSON payload with variable support"
    >
      <FilterLayout>
        <TextAreaAutoResize
          name={`actions.${idx}.webhook_payload_template`}
          label="Payload Template (JSON)"
          placeholder={'{\n  "headline": "{{ .Headline }}",\n  "summary": "{{ .Summary }}",\n  "category": "{{ .Category }}"\n}'}
          tooltip={
            <div>
              <p>JSON template with variable substitution.</p>
              <p className="mt-1">Available variables: .Headline, .Summary, .Category, .StoryID</p>
            </div>
          }
        />
        <PayloadPreview actionType={action.type as "webhook_n8n" | "webhook_cuppa"} />
      </FilterLayout>
    </FilterSection>
  </>
);
